/**
 * Centralized button labels - ensures consistent terminology across the app.
 *
 * Use these constants instead of hardcoding button text.
 * This ensures:
 * 1. Consistent terminology (always "Cancel" not sometimes "Close")
 * 2. Easy updates (change in one place)
 * 3. Loading states match their normal states
 *
 * @example
 * import { buttonLabels } from '@/constants';
 * <Button>{loading ? buttonLabels.loading.saving : buttonLabels.primary.save}</Button>
 */

import type { InvitationFailureReason } from '@/types/assignment';

export const buttonLabels = {
  /**
   * Primary action buttons (typically green/blue, contained variant)
   * These are the main CTAs that move the user forward.
   */
  primary: {
    save: 'Save',
    create: 'Create',
    confirm: 'Confirm',
    submit: 'Submit',
    send: 'Send',
    sendNow: 'Send Now',
    schedule: 'Schedule',
    apply: 'Apply',
    done: 'Done',
    ok: 'OK',
    yes: 'Yes',
    continue: 'Continue',
    next: 'Next',
    finish: 'Finish',
    publish: 'Publish',
    assign: 'Assign',
    merge: 'Merge',
    resolve: 'Resolve',
    complete: 'Complete',
    activate: 'Activate',
    enable: 'Enable',
    start: 'Start',
    signIn: 'Sign In',
    signUp: 'Sign Up',
    resetPassword: 'Reset Password',
    updatePassword: 'Update Password',
    sendLink: 'Send Link',
    resend: 'Resend',
  },

  /**
   * Destructive action buttons (typically red)
   * These buttons perform potentially harmful actions.
   */
  destructive: {
    delete: 'Delete',
    remove: 'Remove',
    archive: 'Archive',
    unassign: 'Unassign',
    revoke: 'Revoke',
    disconnect: 'Disconnect',
    clear: 'Clear',
    clearAll: 'Clear All',
    reset: 'Reset',
    deactivate: 'Deactivate',
    disable: 'Disable',
  },

  /**
   * Secondary/cancel buttons (typically gray/outlined)
   * These buttons dismiss or go back without taking action.
   */
  secondary: {
    cancel: 'Cancel',
    close: 'Close',
    back: 'Back',
    no: 'No',
    skip: 'Skip',
    later: 'Later',
    dismiss: 'Dismiss',
    discard: 'Discard',
    goBack: 'Go Back',
    notNow: 'Not Now',
    maybeLater: 'Maybe Later',
  },

  /**
   * Neutral action buttons (typically text or outlined)
   * These buttons perform neutral actions.
   */
  neutral: {
    edit: 'Edit',
    view: 'View',
    preview: 'Preview',
    download: 'Download',
    export: 'Export',
    import: 'Import',
    copy: 'Copy',
    copyLink: 'Copy Link',
    share: 'Share',
    revise: 'Revise',
    recreate: 'Recreate',
    assignInvite: 'Assign/Invite',
    remind: 'Remind',
    setComplete: 'Set Complete',
    link: 'Link',
    qrCode: 'QR Code',
    resetSearch: 'Reset Search',
    unarchive: 'Unarchive',
    restore: 'Restore',
    retry: 'Retry',
    search: 'Search',
    filter: 'Filter',
    sort: 'Sort',
    more: 'More',
    showMore: 'Show More',
    showLess: 'Show Less',
    showAll: 'Show All',
    hideAll: 'Hide All',
    expand: 'Expand',
    collapse: 'Collapse',
    details: 'Details',
    hideDetails: 'Hide Details',
    showDetails: 'Show Details',
    viewAll: 'View All',
    seeAll: 'See All',
    learnMore: 'Learn More',
    change: 'Change',
    update: 'Update',
    browse: 'Browse',
    upload: 'Upload',
    chooseFile: 'Choose File',
    chooseDifferentFile: 'Choose Different File',
    addNew: 'Add New',
    addAnother: 'Add Another',
    reschedule: 'Reschedule',
    replace: 'Replace',
    tryAgain: 'Try Again',
    generate: 'Generate',
    generateExport: 'Generate Export',
    addLabels: 'Add Labels',
    applyLabels: 'Apply Labels',
    dontSuggestAgain: "Don't Suggest Again",
    changeOwner: 'Change Owner',
    copied: 'Copied!',
    uploadAnother: 'Upload Another',
  },

  /**
   * Loading state labels (shown during async operations)
   * These replace the normal button text during loading.
   */
  loading: {
    saving: 'Saving...',
    creating: 'Creating...',
    deleting: 'Deleting...',
    removing: 'Removing...',
    archiving: 'Archiving...',
    sending: 'Sending...',
    scheduling: 'Scheduling...',
    rescheduling: 'Rescheduling...',
    exporting: 'Exporting...',
    importing: 'Importing...',
    loading: 'Loading...',
    processing: 'Processing...',
    uploading: 'Uploading...',
    downloading: 'Downloading...',
    submitting: 'Submitting...',
    publishing: 'Publishing...',
    merging: 'Merging...',
    resolving: 'Resolving...',
    updating: 'Updating...',
    signing: 'Signing in...',
    resetting: 'Resetting...',
    activating: 'Activating...',
    deactivating: 'Deactivating...',
    connecting: 'Connecting...',
    verifying: 'Verifying...',
    applying: 'Applying...',
    changing: 'Changing...',
    generating: 'Generating...',
    restoring: 'Restoring...',
  },

  /**
   * Selection action buttons
   */
  selection: {
    selectAll: 'Select All',
    deselectAll: 'Deselect All',
    clearSelection: 'Clear Selection',
    selectNone: 'Select None',
    invertSelection: 'Invert Selection',
  },

  /**
   * Navigation buttons
   */
  navigation: {
    home: 'Home',
    dashboard: 'Dashboard',
    settings: 'Settings',
    profile: 'Profile',
    help: 'Help',
    logout: 'Log Out',
    signOut: 'Sign Out',
  },

  /**
   * Toggle state labels
   */
  toggle: {
    on: 'On',
    off: 'Off',
    enabled: 'Enabled',
    disabled: 'Disabled',
    active: 'Active',
    inactive: 'Inactive',
    show: 'Show',
    hide: 'Hide',
  },

  /**
   * Dialog-specific labels
   */
  dialog: {
    done: 'Done',
    showDetails: 'Show Details',
    hideDetails: 'Hide Details',
    next: 'Next',
  },

  /**
   * Tooltip labels for header/toolbar buttons
   */
  tooltips: {
    /** Contacts header actions */
    exportAnalytics: 'Export analytics',
    manageContactLabels: 'Manage contact labels',
    changeOwner: 'Change owner of selected contacts',
    sendLoginLink: 'Send login link',
    archiveSelected: 'Archive selected contacts',
    unarchiveSelected: 'Unarchive selected contacts',
    saveCustomFilters: 'Save custom filters',
    mergeContacts: 'Merge contacts',
    add: 'Add',
    /** Contact card warnings */
    noActiveContactMethods: 'No active contact methods',
    doNotContact: 'Do Not Contact',
    /** Analytics table actions */
    downloadPdf: 'Download PDF',
    downloadCertificate: 'Download Certificate',
    /** Status indicators */
    resolved: 'Resolved',
    flagged: 'Flagged',
    invitationFailed: 'Invitation failed',
    invitedViaEmail: 'Invited via email',
    invitedViaSms: 'Invited via SMS',
    invitedViaApp: 'Invited via app',
    viewedViaLink: 'Viewed via link',
    identityVerified: 'Identity Verified',
    pendingAssignment: 'Pending — will appear once sent',
  },

  /**
   * Integration-specific button labels
   */
  integrations: {
    syncNow: 'Sync Now',
    syncing: 'Syncing...',
    retry: 'Retry',
    editMappings: 'Edit Mappings',
    disconnect: 'Disconnect',
    viewDetails: 'View Details',
    backToIntegrations: 'Back to Integrations',
    manageIntegrations: 'Manage Integrations',
  },
} as const;

/**
 * Type for button label categories
 */
export type ButtonCategory = keyof typeof buttonLabels;

/**
 * Helper to get loading label for a primary action
 */
export function getLoadingLabel(
  primaryLabel: string
): string {
  const loadingMap: Record<string, string> = {
    [buttonLabels.primary.save]: buttonLabels.loading.saving,
    [buttonLabels.primary.create]: buttonLabels.loading.creating,
    [buttonLabels.primary.send]: buttonLabels.loading.sending,
    [buttonLabels.primary.schedule]: buttonLabels.loading.scheduling,
    [buttonLabels.primary.submit]: buttonLabels.loading.submitting,
    [buttonLabels.primary.publish]: buttonLabels.loading.publishing,
    [buttonLabels.primary.merge]: buttonLabels.loading.merging,
    [buttonLabels.primary.resolve]: buttonLabels.loading.resolving,
    [buttonLabels.destructive.delete]: buttonLabels.loading.deleting,
    [buttonLabels.destructive.remove]: buttonLabels.loading.removing,
    [buttonLabels.destructive.archive]: buttonLabels.loading.archiving,
    [buttonLabels.neutral.export]: buttonLabels.loading.exporting,
    [buttonLabels.neutral.import]: buttonLabels.loading.importing,
    [buttonLabels.neutral.upload]: buttonLabels.loading.uploading,
    [buttonLabels.neutral.download]: buttonLabels.loading.downloading,
    [buttonLabels.neutral.update]: buttonLabels.loading.updating,
  };

  return loadingMap[primaryLabel] || buttonLabels.loading.processing;
}

/**
 * Copy for each invitation failure reason (vingapi #837 2b; PM-final copy
 * table 2026-09-24, vingapi docs/specs/837-2b-1a.md R1). A Record over the
 * wire enum, so a new server value fails tsc until it has copy here.
 * Sits next to `tooltips.invitationFailed` — the roster's other failure copy.
 */
export const INVITATION_FAILURE_REASON_COPY: Record<InvitationFailureReason, string> = {
  no_method: 'no email or phone on file',
  no_usable_method: 'no working email or phone',
  email_invalid: 'email address invalid',
  email_bounced: 'email bounced',
  email_deactivated: 'email deactivated',
  phone_invalid: 'number invalid',
  phone_rejected: 'number rejected',
  phone_deactivated: 'number deactivated',
  opted_out: 'opted out',
  delivery_failed: 'delivery failed',
};

/** Reasons that name no address or number, so a target is never shown. */
const TARGETLESS_REASONS: ReadonlySet<InvitationFailureReason> = new Set(['no_method', 'no_usable_method']);

/**
 * The roster / detail-dialog caption for an unreachable contact:
 * `can't be reached · <copy>` then `: <target>` when there is one. The ONE copy
 * source for both surfaces (#875b). Null when there is no reason (a reachable
 * row, or an older API that sends no reason).
 */
export function unreachableCaption(
  reason: InvitationFailureReason | null | undefined,
  target: string | null | undefined,
): string | null {
  if (reason == null) return null;
  // The mapper normalizes unknown wire values; this fallback keeps a caller
  // that skipped it from rendering "undefined".
  const copy = INVITATION_FAILURE_REASON_COPY[reason] ?? INVITATION_FAILURE_REASON_COPY.delivery_failed;
  const base = `can't be reached · ${copy}`;
  return target && !TARGETLESS_REASONS.has(reason) ? `${base}: ${target}` : base;
}

/**
 * Wire value → a known reason. An unknown string (a newer API) maps to
 * 'delivery_failed' for copy purposes rather than throwing; absent/null → null.
 */
export function toInvitationFailureReason(raw: string | null | undefined): InvitationFailureReason | null {
  if (raw == null) return null;
  return Object.prototype.hasOwnProperty.call(INVITATION_FAILURE_REASON_COPY, raw)
    ? (raw as InvitationFailureReason)
    : 'delivery_failed';
}
